import React, {useCallback, useState} from 'react';
import {NumericFormat} from 'react-number-format';
import moment from 'moment';

import {SlicedText} from '../messages';
import {formatDuration, formatNumberToFix} from '../../utils/hooks';
import {SimpleTooltip} from '../tooltip';
import {Images, useSecondaryModal, renderClientName, renderStaffName, sanitizeHtml} from 'utils';
import {ScheduleDetails} from 'fragments';

export const FullNameRow = ({firstName, middleName, lastName, clientCode, textWidth, color, wrapperStyle}) => {
    const [currentWidth, setCurrentWidth] = useState(null);

    const curr = useCallback((node) => {
        if (node !== null) {
            setCurrentWidth(node.getBoundingClientRect().width);
        }
    }, []);

    const width = currentWidth ? currentWidth : 'auto';
    const text = `${clientCode ? `(${clientCode})` : ''} ${lastName ? `${lastName},` : ''} ${firstName ? firstName : ''}  ${middleName ? `${middleName?.slice(0, 1)}.` : ''} `;

    return (
        <div ref={curr} style={{...wrapperStyle}} className="text-style">
            {RenderText(width, text, textWidth, color)}
        </div>
    );
};

export const TextRow = ({name, textWidth, color, wrapperStyle, fontWeight, toolText}) => {
    const [currentWidth, setCurrentWidth] = useState(null);

    const curr = useCallback((node) => {
        if (node !== null) {
            setCurrentWidth(node.getBoundingClientRect().width);
        }
    }, []);

    const width = currentWidth ? currentWidth : 'auto';
    const text = (name ? name : '...') + '';
    return (
        <div ref={curr} className="text-style" style={{...wrapperStyle}}>
            {RenderText(width, text, textWidth, color, fontWeight, toolText)}
        </div>
    );
};

const hasDecimal = (number) => {
    return number.toString().includes('.');
};
export const FixDecimalToTree = ({number}) => {
    if (number) {
        // const num = hasDecimal(number) ? number.toFixed(3) : number;
        const num = hasDecimal(number) ? formatNumberToFix(number) : number;
        if (num) {
            return num;
        } else {
            return '--';
        }
    } else {
        return '--';
    }
};
export const LinkRow = ({
                            client,
                            staff,
                            name,
                            textWidth,
                            color,
                            href,
                            checkPermission,
                            payer,
                            clientCode,
                            defaultColor,
                            wrapperStyle = {},
                        }) => {
    const [currentWidth, setCurrentWidth] = useState(null);

    const curr = useCallback((node) => {
        if (node !== null) {
            setCurrentWidth(node.getBoundingClientRect().width);
        }
    }, []);

    const width = currentWidth ? currentWidth : 'auto';

    const text = name
        ? name
        : client
            ? renderClientName(client)
            : clientCode
                ? clientCode
                : staff
                    ? renderStaffName(staff)
                    : payer
                        ? payer?.name?.slice(0, 4)
                        : '--';

    const handleOpen = (e) => {
        if (((name && name !== '--') || client || staff || clientCode || payer) && href) {
            e.preventDefault();
            e.stopPropagation();
            window.open(href, '_blank', 'noopener,noreferrer');
        }
    };

    const activeStyles = {
        color: checkPermission ? '#347AF0' : defaultColor ? defaultColor : '#4B5C68',
        textDecoration: checkPermission ? 'underline' : 'none',
        width: 'fit-content',
        cursor: checkPermission ? 'pointer' : 'default',
    };

    return (
        <div ref={curr} className="text-style" style={{...wrapperStyle}}>
            <p onClick={(e) => handleOpen(e)} style={activeStyles}>
                {RenderText(width, text, textWidth, color)}
            </p>
        </div>
    );
};

export const ApptDetailRow = ({name, id, showWidth, afterSuccess}) => {
    const [currentWidth, setCurrentWidth] = useState(null);
    const {openSecondary} = useSecondaryModal();

    const curr = useCallback((node) => {
        if (node !== null) {
            setCurrentWidth(node.getBoundingClientRect().width);
        }
    }, []);

    const width = currentWidth ? currentWidth : 'auto';
    const text = (name ? name : '...') + '';

    const handleOpen = () => {
        openSecondary(<ScheduleDetails currentId={id} getAfterSuccess={afterSuccess}/>);
    };

    const activeStyles = {
        color: '#347AF0',
        textDecoration: 'underline',
        width: 'fit-content',
        cursor: 'pointer',
    };

    return (
        <div ref={curr} className="text-style" style={{width: showWidth ? showWidth : 'fit-content'}}>
            <p onClick={(e) => handleOpen(e)} style={activeStyles}>
                {text}
            </p>
        </div>
    );
};

export const IconAndTitle = ({icon, title, textWidth, imgClass, label}) => {
    const [currentWidth, setCurrentWidth] = useState(null);

    const curr = useCallback((node) => {
        if (node !== null) {
            setCurrentWidth(node.getBoundingClientRect().width);
        }
    }, []);

    const width = currentWidth ? currentWidth : 'auto';

    return (
        <div ref={curr} className={`info-and-image ${label}`}>
            <img style={{margin: '0 8px 0 0'}} className={imgClass ? `${imgClass}` : ''} src={icon} alt=""/>
            <div style={{fontWeight: 'bold'}} className="full-width">
                {RenderText(width, title, textWidth)}
            </div>
        </div>
    );
};

export const IconAndTitleSub = ({icon, title, sub, textWidth, imgClass, label}) => {
    const [currentWidth, setCurrentWidth] = useState(null);

    const curr = useCallback((node) => {
        if (node !== null) {
            setCurrentWidth(node.getBoundingClientRect().width);
        }
    }, []);

    const width = currentWidth ? currentWidth : 'auto';

    return (
        <div ref={curr} className={`info-and-image ${label}`} style={{alignItems: 'flex-start'}}>
            <img style={{margin: '0 8px 0 0'}} className={imgClass ? `${imgClass}` : ''} src={icon} alt="Icon"/>
            <div>
                <div className="title-text">{title}</div>
                <div className="subtitle-text">{RenderText(width, sub, textWidth)}</div>
            </div>
        </div>
    );
};
export const IconAndImage = ({item, title, textWidth, avatar}) => {
    const [currentWidth, setCurrentWidth] = useState(null);
    const startName = item?.firstName ? item?.firstName?.slice(0, 1) : '';
    const lastName = item?.lastName ? item?.lastName?.slice(0, 1) : '';

    const curr = useCallback((node) => {
        if (node !== null) {
            setCurrentWidth(node.getBoundingClientRect().width);
        }
    }, []);

    const width = currentWidth ? currentWidth : 'auto';

    return (
        <div ref={curr} className="info-and-avatar">
            <div className="avatar-box">{avatar ? <img src={avatar} alt={'avatar'}/> : `${startName}${lastName}`}</div>
            <div style={{fontWeight: 'bold'}} className="full-width">
                {RenderText(width, title, textWidth)}
            </div>
        </div>
    );
};

export const PriceRow = ({info, textWidth}) => {
    const [currentWidth, setCurrentWidth] = useState(null);

    const curr = useCallback((node) => {
        if (node !== null) {
            setCurrentWidth(node.getBoundingClientRect().width);
        }
    }, []);

    const width = currentWidth ? currentWidth : 250;

    // const price = (info ? info.toFixed(3) : 0) + '';
    const price = info === 0 ? '0.00' : (info ? formatNumberToFix(info) : 0.0) + '';

    if (price) {
        const count = Math.ceil(width / (textWidth ? textWidth : 11));
        const slicedText = '' + price?.slice(0, count);

        return (
            <div ref={curr} className="text-style">
                {price?.length > count ? (
                    <SimpleTooltip
                        title={
                            <p>
                                <NumericFormat value={price} displayType={'text'} thousandSeparator={true}
                                               prefix={'$'}/>
                            </p>
                        }
                        placement="top-start"
                    >
                        <p style={{textOverflow: 'ellipsis'}}>
                            <NumericFormat value={slicedText} displayType={'text'} thousandSeparator={true}
                                           prefix={'$'}/>
                            ...
                        </p>
                    </SimpleTooltip>
                ) : (
                    <p>{<NumericFormat value={price} displayType={'text'} thousandSeparator={true} prefix={'$'}/>}</p>
                )}
            </div>
        );
    } else {
        return '...';
    }
};

export const UnitsRow = ({info, textWidth, renderTime}) => {
    const [currentWidth, setCurrentWidth] = useState(null);

    const curr = useCallback((node) => {
        if (node !== null) {
            setCurrentWidth(node.getBoundingClientRect().width);
        }
    }, []);

    const width = currentWidth ? currentWidth : 250;

    const price = (info ? info.toFixed(2) : 0) + '';

    if (price) {
        const count = Math.ceil(width / (textWidth ? textWidth : 11));
        const slicedText = '' + price?.slice(0, count);
        return (
            <div ref={curr} className="text-style">
                {price?.length > count ? (
                    <SimpleTooltip
                        title={
                            <p>
                                <NumericFormat value={price} displayType={'text'} thousandSeparator={true}/>
                            </p>
                        }
                        placement="top-start"
                    >
                        <p style={{textOverflow: 'ellipsis'}}>
                            <NumericFormat value={slicedText} displayType={'text'} thousandSeparator={true}/>
                            ...
                        </p>
                    </SimpleTooltip>
                ) : (
                    <p>{<NumericFormat value={price} displayType={'text'} thousandSeparator={true}/>}</p>
                )}
            </div>
        );
    } else {
        return '...';
    }
};

const convertHours = (minutes) => {
    let hours = Math.trunc(minutes / 60);
    let remainingMinutes = Math.trunc(minutes % 60);
    if (hours === 0) {
        return `0h ${remainingMinutes < 0 ? '-' : ''}${Math.abs(remainingMinutes)}m`;
    } else {
        return `${hours < 0 ? '-' : ''}${Math.abs(hours)}h ${Math.abs(remainingMinutes)}m`;
    }
};

export const UnitHour = ({info, size}) => {
    let hour = size * info;

    if (hour !== 0) {
        return (
            <div className="text-style">
                <p>{hour !== 0 && <p>{convertHours(hour)}</p>}</p>
            </div>
        );
    } else {
        return '...';
    }
};
export const UnitsRowByHour = ({info, textWidth, size, notShowHours}) => {
    const [currentWidth, setCurrentWidth] = useState(null);

    const curr = useCallback((node) => {
        if (node !== null) {
            setCurrentWidth(node.getBoundingClientRect().width);
        }
    }, []);

    const width = currentWidth ? currentWidth : 250;
    const price = (info ? info.toFixed(2) : 0) + '';
    let hour = convertHours(size * info);

    if (price) {
        const count = Math.ceil(width / (textWidth ? textWidth : 11));
        const slicedText = '' + price?.slice(0, count);
        return (
            <div ref={curr} className="text-style">
                {price?.length > count ? (
                    <SimpleTooltip
                        title={
                            <p>
                                <NumericFormat value={price} displayType={'text'} thousandSeparator={true}/>
                                {hour !== 0 && <p>{hour}</p>}
                            </p>
                        }
                        placement="top-start"
                    >
                        <p style={{textOverflow: 'ellipsis'}}>
                            <NumericFormat value={slicedText} displayType={'text'} thousandSeparator={true}/>
                            ...
                        </p>
                    </SimpleTooltip>
                ) : (
                    <p>
                        {!notShowHours && <NumericFormat value={price} displayType={'text'} thousandSeparator={true}/>}
                        {hour !== 0 && <p>{hour}</p>}
                    </p>
                )}
            </div>
        );
    } else {
        return '...';
    }
};

export const UnitsRowByHourOneLine = ({info, textWidth, size, notShowHours}) => {
    const [currentWidth, setCurrentWidth] = useState(null);

    const curr = useCallback((node) => {
        if (node !== null) {
            setCurrentWidth(node.getBoundingClientRect().width);
        }
    }, []);

    const width = currentWidth ? currentWidth : 250;
    const price = (info ? info.toFixed(2) : 0) + '';
    let hour = size * info;
    const hoursCoverted = size ? convertHours(hour) : '';
    const mixed = `${price} ${hoursCoverted}`;

    if (mixed) {
        const count = Math.ceil(width / (textWidth ? textWidth : 11));
        const slicedText = '' + mixed?.slice(0, count);
        return (
            <div ref={curr} className="text-style">
                {mixed?.length > count ? (
                    <SimpleTooltip
                        title={
                            <p>
                                <NumericFormat value={mixed} displayType={'text'} thousandSeparator={true}/>
                                {hour !== 0 && <p>{hoursCoverted}</p>}
                            </p>
                        }
                        placement="top-start"
                    >
                        <p style={{textOverflow: 'ellipsis'}}>
                            <NumericFormat value={slicedText} displayType={'text'} thousandSeparator={true}/>
                            ...
                        </p>
                    </SimpleTooltip>
                ) : (
                    <p>{mixed}</p>
                )}
            </div>
        );
    } else {
        return '...';
    }
};

export const DateRow = ({date}) => {
    return date ? moment.utc(date).format('MM/DD/YYYY') : '--';
};

export const LocalDateRow = ({date}) => {
    return date ? `${moment(date).local().format('MM/DD/YYYY, h:mm A')}` : '--';
};
export const DateTimeRow = ({date}) => {
    return date ? <div>{`${moment.utc(date).format('MM/DD/YYYY')} ${moment(date).format('LT')}`}</div> : '--';
};

export const DateRowStartEnd = ({start, end, format, secondLine}) => {
    if (!start && !end) {
        return '-'
    }
    if (secondLine) {
        return (
            <div>
                <p>
                    <DateRow date={start}/>
                </p>
                <p>
                    <DateRow date={end}/>
                </p>
            </div>
        );
    } else {
        return `${moment.utc(start).format(format ? format : 'MM/DD/YYYY')} - ${moment
            .utc(end)
            .format(format ? format : 'MM/DD/YYYY')}`;
    }
};

export const IdRow = ({id, size}) => {
    return <SlicedText type={'id'} size={size ? size : 10} data={id}/>;
};

export const DisplayIdRow = ({id}) => {
    return <p style={{whiteSpace: 'nowrap'}}>{id ? id : '...'}</p>;
};

export const DisplayIdAnFlagRow = ({id, title, flag}) => {
    return (
        <div className="flex-align-center" style={{gap: 8}}>
            <p>{id ? id : '...'}</p>
            {flag && (
                <SimpleTooltip
                    title={
                        <p>
                            {title
                                ? title
                                : 'One or more appointments in this claim have incomplete units. Please review appointment(s) before submitting claim.'}
                        </p>
                    }
                    placement="bottom"
                    arrow
                >
                    <img src={Images.flag} alt="flag"/>
                </SimpleTooltip>
            )}
        </div>
    );
};

export const ApptFlags = ({id, updateFlag, incomplete, removed, link, singleAppt, fromAppt}) => {
    return (
        <div className="flex-align-center" style={{gap: 8}}>
            {link ? (
                <a href="/schedule" style={{color: '#347AF0', whiteSpace: 'nowrap'}}>
                    {id}
                </a>
            ) : (
                <p style={{whiteSpace: 'nowrap', width: 'fit-content'}}>{id ? id : ''}</p>
            )}
            {!fromAppt && updateFlag && (
                <div style={{width: 20}}>
                    <SimpleTooltip
                        title={
                            <p>
                                {singleAppt
                                    ? 'This appointment has been updated.'
                                    : 'One or more appointments in this claim have been updated. Please review appointment(s) before submitting claim.'}
                            </p>
                        }
                        placement="bottom"
                        arrow
                    >
                        <img src={Images.yellowFlag} alt="flag"/>
                    </SimpleTooltip>
                </div>
            )}
            {incomplete && (
                <div style={{width: 20}}>
                    <SimpleTooltip
                        title={
                            <p>
                                {singleAppt
                                    ? 'This appointment has incomplete units.'
                                    : 'One or more appointments in this claim have incomplete units. Please review appointment(s) before submitting claim.'}
                            </p>
                        }
                        placement="bottom"
                        arrow
                    >
                        <img src={Images.flag} alt="flag"/>
                    </SimpleTooltip>
                </div>
            )}
            {removed && (
                <div style={{width: 20}}>
                    <SimpleTooltip title={<p>{'Appointment was removed from claim'}</p>} placement="bottom" arrow>
                        <img src={Images.blueFlag} alt="flag"/>
                    </SimpleTooltip>
                </div>
            )}
        </div>
    );
};

export const BatchFlagRow = ({id, flag, notShowFlag, notShowResolve, resolve, handleResolve}) => {
    return (
        <div className="flex-align-center" style={{gap: 8}}>
            <p>{id ? id : '--'}</p>
            <div style={{width: 20}}>
                {resolve && (
                    <div className="resolved-btn-box">
                        <img src={Images.greenMark} alt="greenMark"/>
                        <p>Resolved</p>
                    </div>
                )}
                {flag && !resolve && (
                    <div className="flex-align-center" style={{gap: '8px'}}>
                        {!notShowFlag && (
                            <SimpleTooltip title={<p>{'This appointment has incomplete units.'}</p>} placement="bottom"
                                           arrow>
                                <img src={Images.yellowFlag} alt="flag"/>
                            </SimpleTooltip>
                        )}
                        {!notShowResolve && (
                            <button onClick={handleResolve} className="resolve-btn-box">
                                Resolve
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export const PhoneRow = ({phone}) => {
    const converted = phone ? '(' + phone.substr(0, 3) + ') ' + phone.substr(3, 3) + '-' + phone.substr(6, 6) : '--';
    return converted;
};

export const TypeRow = ({text}) => {
    if (text === null || text === undefined || text === '') {
        return '...';
    }
    // `\w` includes `_`, so `(\w)(\w*)` treated `OFFICE_ALLY` as one token and
    // only capitalized the first letter (→ "Office_ally"). Split on underscores
    // (and existing whitespace) and title-case each word: "OFFICE_ALLY" → "Office Ally".
    return String(text)
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

const RenderText = (width, text, textWidth, color, fontWeight, toolText) => {
    const count = Math.ceil(width / (textWidth ? textWidth : 9));
    const slicedText = '' + text?.slice(0, count);

    if (toolText) {
        return (
            <SimpleTooltip title={<p className="show-tool-text-style">{toolText}</p>} placement="top-start">
                <p
                    style={{width: width, wordWrap: 'break-word', fontWeight: fontWeight ? fontWeight : 'normal'}}
                    className="show-text-style"
                >
                    {text?.length > count ? `${slicedText}...` : slicedText}
                </p>
            </SimpleTooltip>
        )
    } else if (text) {
        return (
            <>
                {text?.length > count ? (
                    <SimpleTooltip title={<p className="show-tool-text-style">{text}</p>} placement="top-start">
                        <p
                            style={{
                                width: width,
                                wordWrap: 'break-word',
                                fontWeight: fontWeight ? fontWeight : 'normal'
                            }}
                            // style={color ? { color: color } : {}}
                            className="show-text-style"
                        >
                            {`${slicedText}...`}
                        </p>
                    </SimpleTooltip>
                ) : (
                    <p style={color ? {color: color} : {fontWeight: fontWeight ? fontWeight : 'normal'}}>{text}</p>
                )}
            </>
        );
    } else {
        return '...';
    }
};

export const StatusRow = ({status, noCircle}) => {
    const renderCircle =
        status === 'ACTIVE'
            ? '#6FD231'
            : status === 'FBA'
                ? '#ADD8E6'
                : status === 'FBS'
                    ? '#ADD8E6'
                    : status === 'ON_HOLD'
                        ? '#FFCA33'
                        : status === 'HOLD'
                            ? '#F79009'
                            : status === 'TERMINATED'
                                ? '#FE7070'
                                : status === 'WAIT_LIST'
                                    ? '#FE7B43'
                                    : status === 'GRADUATED'
                                        ? '#51439E'
                                        : status === 'INACTIVE'
                                            ? '#98A2B3'
                                            : status === 'ON_LEAVE'
                                                ? '#0BA5EC'
                                                : status === 'INVITED' ?
                                                    '#F8F9FC' : '';

    const renderText =
        status === 'ACTIVE'
            ? 'Active'
            : status === 'FBA'
                ? 'FBA'
                : status === 'FBS'
                    ? 'FBA'
                    : status === 'ON_HOLD'
                        ? 'On Hold'
                        : status === 'TERMINATED'
                            ? 'Terminated'
                            : status === 'WAIT_LIST'
                                ? 'Wait List'
                                : status === 'GRADUATED'
                                    ? 'Graduated'
                                    : status === 'INACTIVE'
                                        ? 'Inactive'
                                        : status === 'ON_LEAVE'
                                            ? 'On Leave'
                                            : status === 'HOLD'
                                                ? 'On Hold'
                                                : status === 'INVITED' ?
                                                    'Intake' : '';

    return (
        <div className="flex-align-center">
            {!noCircle && <div className={`circle-box`} style={{background: renderCircle}}/>}
            <p className="status-circle-text">{renderText}</p>
        </div>
    );
};

export const renderCptCodeAndModifiers = (cptCode, chargeRateModifier, chargeRateModifier2, chargeRateModifier3, posModifier) => {
    if (cptCode) {
        const mods = [chargeRateModifier, chargeRateModifier2, chargeRateModifier3, posModifier].filter(Boolean);
        return mods.length ? `${cptCode} (${mods.join(', ')})` : cptCode;
    } else {
        return '--';
    }
};

export const AgingRow = ({count}) => {
    if (count < 30) {
        return <div className="green-zone-row">{count}</div>;
    } else if (count > 30 && count < 60) {
        return <div className="yellow-zone-row">{count}</div>;
    } else if (count > 60) {
        return <div className="red-zone-row">{count}</div>;
    } else {
        return '--';
    }
};

export const AgingRowBackground = (count) => {
    if (count < 30) {
        return '#ECFDF3';
    } else if (count > 30 && count < 60) {
        return '#FFF6ED';
    } else if (count > 60) {
        return '#FEF3F2';
    } else {
        return '';
    }
};

const promptTypeEnums = {
    Full_Physical: 'Full Physical',
    No_response: 'No response',
};

export const RenderPrompt = (type) => {
    switch (type) {
        case promptTypeEnums.Full_Physical:
            return <div className="full-physical-box">Full Physical</div>;
        case promptTypeEnums.No_response:
            return <div className="no-response-box">Full Physical</div>;

        default:
            return type;
    }
};

export const HtmlConvertor = ({text, height}) => {
    return (
        <div>
            {text &&
                <SimpleTooltip
                    title={<div style={{color: 'white'}} className="richtext-readonly-content"
                                dangerouslySetInnerHTML={{__html: sanitizeHtml(text)}}/>}
                    arrow
                    placement="top"
                >
                    <div
                        style={{height: height ? height : 40, overflow: 'scroll'}}
                        className="richtext-readonly-content"
                        dangerouslySetInnerHTML={{__html: sanitizeHtml(text)}}
                    />
                </SimpleTooltip>
            }
        </div>
    );
};
